/**
 * 이해관계자 등록부와 RACI.
 * 한 WBS 항목의 A(Accountable, 책임)는 한 사람만 둔다.
 */
import type { Project, RaciAssignment, RaciLetter, Stakeholder, StakeholderLevel, Task } from "@/engine/types";

export const STAKEHOLDER_LEVELS: StakeholderLevel[] = ["low", "medium", "high"];
export const STAKEHOLDER_LEVEL_LABEL: Record<StakeholderLevel, string> = { low: "낮음", medium: "보통", high: "높음" };

export const RACI_LETTERS: RaciLetter[] = ["R", "A", "C", "I"];
export const RACI_LABEL: Record<RaciLetter, string> = {
  R: "수행",
  A: "책임",
  C: "자문",
  I: "공유",
};

/** 메뉴에서 고를 때 보여주는 한 줄. 도움말의 뜻과 같다. */
export const RACI_MEANING: Record<RaciLetter, string> = {
  R: "그 일을 합니다.",
  A: "결과를 맡습니다. 한 항목에 한 사람입니다.",
  C: "결정 전에 묻고, 답을 받습니다.",
  I: "결정이 난 뒤 결과를 전달받습니다.",
};

export const RACI_LEGEND = RACI_LETTERS.map((letter) => `${letter} ${RACI_LABEL[letter]}`).join(" · ");

export function stakeholderLevels(value: unknown): StakeholderLevel | undefined {
  return value === "low" || value === "medium" || value === "high" ? value : undefined;
}

export function raciTargets(tasks: readonly Task[]): Task[] {
  const parents = new Set(tasks.map((task) => task.parentId).filter((id): id is string => !!id));
  return tasks.filter((task) => parents.has(task.id) || !!task.deliverable?.trim());
}

export function letterAt(raci: readonly RaciAssignment[], taskId: string, stakeholderId: string): RaciLetter | null {
  return raci.find((cell) => cell.taskId === taskId && cell.stakeholderId === stakeholderId)?.letter ?? null;
}

/** A는 그 항목의 기존 책임을 밀어낸다. letter가 null이면 그 칸을 지운다. */
export function setRaciLetter(raci: readonly RaciAssignment[], taskId: string, stakeholderId: string, letter: RaciLetter | null): RaciAssignment[] {
  const rest = raci.filter((cell) => !(cell.taskId === taskId && cell.stakeholderId === stakeholderId));
  const withoutOtherA = letter === "A" ? rest.filter((cell) => !(cell.taskId === taskId && cell.letter === "A")) : rest;
  if (!letter) return withoutOtherA;
  return [...withoutOtherA, { stakeholderId, taskId, letter }];
}

export function accountableOf(project: Pick<Project, "stakeholders" | "raci">, taskId: string): Stakeholder | null {
  const cell = (project.raci ?? []).find((row) => row.taskId === taskId && row.letter === "A");
  if (!cell) return null;
  return (project.stakeholders ?? []).find((person) => person.id === cell.stakeholderId) ?? null;
}

/** 주간 보고에 적는 책임자 이름. 없으면 null. */
export function accountableName(project: Pick<Project, "stakeholders" | "raci">, taskId: string): string | null {
  const name = accountableOf(project, taskId)?.name.trim();
  return name || null;
}

export function dropStakeholder(project: Pick<Project, "stakeholders" | "raci">, stakeholderId: string): { stakeholders: Stakeholder[]; raci: RaciAssignment[] } {
  return {
    stakeholders: (project.stakeholders ?? []).filter((person) => person.id !== stakeholderId),
    raci: (project.raci ?? []).filter((cell) => cell.stakeholderId !== stakeholderId),
  };
}
